import React, { useState } from "react";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import { Edit, FileText, AlertCircle, Users, Trash2, Flag } from "lucide-react";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { RequestService } from "services/RequestService";
import { Grid, Stack, Divider } from "@mui/material";

const ProjectActions = ({ projectId }) => {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [error, setError] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [projectIdToDelete, setProjectIdToDelete] = useState(null);

  const toggleSnackbar = () => setShow(!show);
  const toggleSnackbar2 = () => setError(!error);

  const handleOpenDialog = (id) => {
    setProjectIdToDelete(id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setProjectIdToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (projectIdToDelete) {
      try {
        await RequestService.delete(`Project/${projectIdToDelete}`);
        toggleSnackbar();
        handleCloseDialog();
        setTimeout(() => {
          navigate("/pages/projects/viewprojects");
        }, 1000);
      } catch (error) {
        console.error("Error deleting project:", error);
        toggleSnackbar2();
        handleCloseDialog();
      }
    }
  };

  const actionButtons = [
    {
      icon: <Edit size={20} />,
      color: "info",
      label: { en: "Edit", ar: "تعديل" },
      onClick: () => navigate(`/projects/project/edit?id=${projectId}`),
    },
    {
      icon: <Flag size={20} />,
      color: "info",
      label: { en: "Milestones", ar: "المراحل" },
      onClick: () => navigate(`/pages/projects/milestones/view?id=${projectId}`),
    },
    {
      icon: <AlertCircle size={20} />,
      color: "warning",
      label: { en: "Issues", ar: "المعوقات" },
      onClick: () => navigate(`/pages/projects/issues/view?id=${projectId}`),
    },
    {
      icon: <FileText size={20} />,
      color: "error",
      label: { en: "Risks", ar: "المخاطر" },
      onClick: () => navigate(`/pages/projects/risks/view?id=${projectId}`),
    },
    {
      icon: <Users size={20} />,
      color: "info",
      label: { en: "Project Managers", ar: "مديري المشاريع" },
      onClick: () => navigate(`/pages/projects/EditProjectManagers?id=${projectId}`),
    },
    {
      icon: <Trash2 size={20} />,
      color: "error",
      label: { en: "Delete", ar: "حذف" },
      onClick: () => handleOpenDialog(projectId),
    },
  ];

  return (
    <Card sx={{ p: 3, width: "100%", boxShadow: 2 }}>
      <MDSnackbar
        color="success"
        icon="check_circle"
        title="Operation Completed / تم اتمام العملية"
        content="Operation completed successfully / تم اتمام العملية بنجاح"
        dateTime="0s ago / منذ 0 ثانية"
        open={show}
        close={toggleSnackbar}
      />

      <MDSnackbar
        color="error"
        icon="error"
        title="Error / خطأ"
        content="An error occurred / حدث خطأ ما"
        dateTime="0s ago / منذ 0 ثانية"
        open={error}
        close={toggleSnackbar2}
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="confirm-delete-dialog-title"
      >
        <DialogTitle id="confirm-delete-dialog-title">
          Delete Confirmation / تأكيد الحذف
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action cannot be undone. Are you sure?
            <Divider sx={{ my: 1 }} />
            سيتم حذف بيانات هذا المشروع بشكل نهائي. هل أنت متأكد؟
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleConfirmDelete} color="error">
            Yes / نعم
          </MDButton>
          <MDButton onClick={handleCloseDialog} color="secondary">
            No / لا
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDBox mb={3}>
        <MDTypography variant="h6" fontWeight="medium">
          Project Actions / إجراءات المشروع
        </MDTypography>
      </MDBox>

      <Grid container spacing={2}>
        {actionButtons.map((button, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <MDButton
              variant="gradient"
              color={button.color}
              onClick={button.onClick}
              fullWidth
              sx={{
                py: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  transition: 'transform 0.2s ease-in-out',
                },
              }}
            >
              {button.icon}
              <Stack alignItems="center" spacing={0.5}>
                <MDTypography variant="button" color="white" fontWeight="regular">
                  {button.label.en}
                </MDTypography>
                <MDTypography variant="caption" color="white" fontWeight="regular">
                  {button.label.ar}
                </MDTypography>
              </Stack>
            </MDButton>
          </Grid>
        ))}
      </Grid>
    </Card>
  );
};

ProjectActions.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ProjectActions;
